/**
 * Route Analysis Inference Plugin
 *
 * Two modes:
 *   1. GeoTIFF DEM → /route/analyze (from existing scene object)
 *   2. Lat/lon → /route/analyze-location (fetches DEM server-side)
 *
 * Both return visibility heatmap + least-visible route + optional GLBs.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { Utility } from 'trace-viewer';
import { CreateObjectCommand, getUndoManager, topLevelParent } from 'trace-viewer/editor';
import type { SceneObjectData } from 'trace-viewer';
import type { ToolContext } from '../ai/agent-helpers';
import { applyTransform, resolveObject } from '../ai/agent-helpers';
import { declareInferencePlugin } from '../plugins/inference-adapter';
import {
    INFERENCE_API_URL,
    getGeoTiffBlob,
    inferencePost,
    isGeoTiffSceneObject,
    type InferenceStatusCallback,
} from './shared';

// ── Response shapes ───────────────────────────────────────

interface RouteWaypoint {
    row: number;
    col: number;
}

interface GeoPoint {
    lat: number;
    lon: number;
}

export interface RouteAnalyzeResponse {
    session_id?: string;
    dem?: {
        original_shape?: number[];
        analysis_shape?: number[];
        elevation_min?: number;
        elevation_max?: number;
    };
    visibility?: {
        algorithm?: string;
        mean_score?: number;
        max_score?: number;
    };
    route?: {
        start?: number[];
        stop?: number[];
        start_geo?: GeoPoint;
        stop_geo?: GeoPoint;
        waypoints?: RouteWaypoint[];
        num_waypoints?: number;
        cost?: number;
        distance_km?: number;
    };
    parameters?: Record<string, unknown>;
    timings?: Record<string, number>;
    glb?: {
        terrain_glb?: string;
        route_glb?: string;
        terrain_glb_size_kb?: number;
        route_glb_size_kb?: number;
    };
}

export interface RerouteResponse {
    route: {
        start: number[];
        stop: number[];
        start_geo?: GeoPoint;
        stop_geo?: GeoPoint;
        waypoints: RouteWaypoint[];
        num_waypoints: number;
        cost: number;
        distance_km?: number;
    };
    glb?: {
        route_glb: string;
        route_glb_size_kb: number;
    };
}

export interface GeocodeCandidate {
    place: string;
    lat: number;
    lon: number;
    osm_type: string;
    osm_class: string;
    bbox: [number, number, number, number]; // [west, south, east, north]
    bbox_source: string;
    radius_km: number;
}

export interface GeocodeResponse {
    query: string;
    candidates: GeocodeCandidate[];
}

// ── Shared route params ───────────────────────────────────

export interface RouteParams {
    gridSize?: number;
    observerHeight?: number;
    foliageThresh?: number;
    startLat?: number;
    startLon?: number;
    startRow?: number;
    startCol?: number;
    stopLat?: number;
    stopLon?: number;
    stopRow?: number;
    stopCol?: number;
    vertExag?: number;
    tableWidth?: number;
    cellMeters?: number;
}

export const ROUTE_WARMUP = {
    healthReady: (body: Record<string, unknown>) => body.status === 'ok',
};

export function appendRouteParams(formData: FormData, params: RouteParams): void {
    if (params.gridSize != null) formData.append('grid_size', String(params.gridSize));
    if (params.observerHeight != null) formData.append('observer_height', String(params.observerHeight));
    if (params.foliageThresh != null) formData.append('foliage_thresh', String(params.foliageThresh));
    if (params.startLat != null) formData.append('start_lat', String(params.startLat));
    if (params.startLon != null) formData.append('start_lon', String(params.startLon));
    if (params.startRow != null) formData.append('start_row', String(params.startRow));
    if (params.startCol != null) formData.append('start_col', String(params.startCol));
    if (params.stopLat != null) formData.append('stop_lat', String(params.stopLat));
    if (params.stopLon != null) formData.append('stop_lon', String(params.stopLon));
    if (params.stopRow != null) formData.append('stop_row', String(params.stopRow));
    if (params.stopCol != null) formData.append('stop_col', String(params.stopCol));
    if (params.vertExag != null) formData.append('vert_exag', String(params.vertExag));
    if (params.tableWidth != null) formData.append('table_width', String(params.tableWidth));
    if (params.cellMeters != null) formData.append('cell_meters', String(params.cellMeters));
}

export function parseRouteParams(input: Record<string, unknown>): RouteParams {
    const params: RouteParams = {};
    if (input.grid_size != null) params.gridSize = input.grid_size as number;
    if (input.observer_height != null) params.observerHeight = input.observer_height as number;
    if (input.foliage_thresh != null) params.foliageThresh = input.foliage_thresh as number;
    if (input.start_lat != null) params.startLat = input.start_lat as number;
    if (input.start_lon != null) params.startLon = input.start_lon as number;
    if (input.start_row != null) params.startRow = input.start_row as number;
    if (input.start_col != null) params.startCol = input.start_col as number;
    if (input.stop_lat != null) params.stopLat = input.stop_lat as number;
    if (input.stop_lon != null) params.stopLon = input.stop_lon as number;
    if (input.stop_row != null) params.stopRow = input.stop_row as number;
    if (input.stop_col != null) params.stopCol = input.stop_col as number;
    if (input.vert_exag != null) params.vertExag = input.vert_exag as number;
    if (input.table_width != null) params.tableWidth = input.table_width as number;
    if (input.cell_meters != null) params.cellMeters = input.cell_meters as number;
    return params;
}

// ── Helpers ───────────────────────────────────────────────

function base64ToBlob(base64: string, mimeType: string): Blob {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
}

export async function addGlbToScene(
    traceScene: TraceScene,
    glbBlob: Blob,
    baseName: string,
    suffix: string,
    parent?: SceneObject,
): Promise<SceneObject> {
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${baseName}_${suffix}.glb`,
        glbBlob,
    );

    const allObjects = traceScene.getSceneObjects();
    const siblingNames = new Set(allObjects.map(o => o.name));
    const newName = Utility.generateUniqueName(
        `${baseName}_${suffix}`,
        siblingNames,
    );

    const objectData: SceneObjectData = {
        name: newName,
        src: `./${assetPath}`,
        transform: {},
        behaviors: [],
    };

    // No parent given means the top of the scene: the DOCUMENT ROOT (D8).
    const targetParent = parent ?? topLevelParent(traceScene);
    const newObject = await traceScene.createSceneObjectFromData(
        objectData,
        targetParent ?? undefined,
    );

    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(
            new CreateObjectCommand(newObject, targetParent, objectData),
        );
    }

    return newObject;
}

// ── Session tracking ─────────────────────────────────────

/**
 * Module-level map of container object name → session_id.
 * Used by reroute to find the session for a previously created terrain.
 */
const sessionMap = new Map<string, string>();

export function getSessionId(objectName: string): string | undefined {
    return sessionMap.get(objectName);
}

export function storeSessionId(objectName: string, sessionId: string): void {
    sessionMap.set(objectName, sessionId);
}

/**
 * Materialize a RouteAnalyzeResponse into the scene as a container with
 * terrain + route GLB children. Shared by both DEM-upload and location flows.
 */
export async function materializeRouteResult(
    traceScene: TraceScene,
    result: RouteAnalyzeResponse,
    baseName: string,
    onStatus?: InferenceStatusCallback,
): Promise<SceneObject | null> {
    const hasTerrain = !!result.glb?.terrain_glb;
    const hasRoute = !!result.glb?.route_glb;
    if (!hasTerrain && !hasRoute) return null;

    const containerName = Utility.generateUniqueName(
        `${baseName}_route`,
        new Set(traceScene.getSceneObjects().map(o => o.name)),
    );
    const containerData: SceneObjectData = {
        name: containerName,
        transform: {},
        behaviors: [],
    };
    // Top-level content parents under the DOCUMENT ROOT (SCENE_ROOT_SPEC D8).
    const containerParent = topLevelParent(traceScene);
    const container = await traceScene.createSceneObjectFromData(containerData, containerParent ?? undefined);

    try {
        if (hasTerrain) {
            onStatus?.('loading terrain mesh…');
            const terrainBlob = base64ToBlob(result.glb!.terrain_glb!, 'model/gltf-binary');
            await addGlbToScene(traceScene, terrainBlob, baseName, 'terrain', container);
        }

        if (hasRoute) {
            onStatus?.('loading route mesh…');
            const routeBlob = base64ToBlob(result.glb!.route_glb!, 'model/gltf-binary');
            await addGlbToScene(traceScene, routeBlob, baseName, 'route', container);
        }
    } catch (err) {
        // Rollback: delete the empty container so the scene doesn't end up
        // with an orphan after a mid-flight decode/import failure.
        try { traceScene.deleteSceneObject(container); } catch { /* best-effort */ }
        throw err;
    }

    // Only record undo once materialization succeeded — we don't want an
    // undo stack entry for a container that was rolled back.
    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(
            new CreateObjectCommand(container, containerParent, containerData),
        );
    }

    // Track session for rerouting
    if (result.session_id) {
        sessionMap.set(container.name, result.session_id);
    }

    traceScene.select(container);
    return container;
}

// ── Core run function (DEM upload) ────────────────────────

async function runRouteAnalysis(
    sourceObject: SceneObject,
    traceScene: TraceScene,
    params: RouteParams = {},
    onStatus?: InferenceStatusCallback,
): Promise<SceneObject> {
    const demBlob = await getGeoTiffBlob(sourceObject, traceScene);
    const ext = sourceObject.src.split('.').pop()?.toLowerCase() ?? 'tif';

    onStatus?.('analyzing terrain visibility…');

    const response = await inferencePost('route', () => {
        const formData = new FormData();
        formData.append('dem_file', demBlob, `dem.${ext}`);
        formData.append('include_glb', 'true');
        appendRouteParams(formData, params);
        return [`${INFERENCE_API_URL}/route/analyze`, { method: 'POST', body: formData }];
    }, ROUTE_WARMUP, onStatus);

    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Route analysis error (${response.status}): ${errText}`);
    }

    const result = await response.json() as RouteAnalyzeResponse;
    const container = await materializeRouteResult(traceScene, result, sourceObject.name, onStatus);
    if (!container) throw new Error('Route analysis succeeded but returned no GLB meshes.');
    return container;
}

// ── Context-menu run (no extra params) ────────────────────

async function run(
    sourceObject: SceneObject,
    traceScene: TraceScene,
    onStatus?: InferenceStatusCallback,
): Promise<SceneObject> {
    return runRouteAnalysis(sourceObject, traceScene, {}, onStatus);
}

// ── AI Tool Override (supports route params) ──────────────

async function executeRouteAnalyze(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const objectRef = input.object as string;
    const name = input.name as string | undefined;
    const position = input.position as [number, number, number] | undefined;
    const rotation = input.rotation as [number, number, number] | undefined;
    const scale = input.scale as [number, number, number] | undefined;
    const { traceScene } = ctx;

    const target = resolveObject(objectRef, ctx);
    if (!target) return `Error: Object "${objectRef}" not found.`;
    if (!isGeoTiffSceneObject(target)) {
        return `Error: "${objectRef}" is not a GeoTIFF image. Route analysis requires a .tif/.tiff DEM file.`;
    }

    try {
        const result = await runRouteAnalysis(target, traceScene, parseRouteParams(input));
        applyTransform(result.transform, position, rotation, scale);
        if (name) traceScene.renameSceneObject(result, name);
        ctx.onSceneChanged?.();
        const parts: string[] = [];
        for (const child of result.getChildren()) {
            parts.push(child.name);
        }
        return `Created route analysis "${result.name}" from GeoTIFF "${objectRef}". Contains: ${parts.join(', ')}.`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }
}

// ── Plugin Declaration ────────────────────────────────────

declareInferencePlugin({
    id: 'route',
    sourceType: 'geotiff',
    menuLabel: 'Analyze Route (Visibility)',
    menuOrder: 210,

    toolName: 'analyze_route',
    extraInputProperties: {
        grid_size: { type: 'integer' },
        observer_height: { type: 'number' },
        foliage_thresh: { type: 'number' },
        start_lat: { type: 'number' },
        start_lon: { type: 'number' },
        start_row: { type: 'integer' },
        start_col: { type: 'integer' },
        stop_lat: { type: 'number' },
        stop_lon: { type: 'number' },
        stop_row: { type: 'integer' },
        stop_col: { type: 'integer' },
        vert_exag: { type: 'number' },
        table_width: { type: 'number' },
        cell_meters: { type: 'number' },
    },

    run,
    executeToolOverride: executeRouteAnalyze,

    slashCommand: {
        name: 'route',
        description: 'Analyze terrain visibility and compute least-visible route from a GeoTIFF DEM',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
